import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from "react-router-dom";

import fastStorage from '../fastStorage';
import useDirtyCheck from './useDirtyCheck2';
import { getRoutePath } from '../util/routeUtils';
import { checkEntityPropChanges } from '../fms.core/data/ChangeTrackable';

/**
 * Called from 'add' screen to generate full fast storage keys for 'edit' screens to be able to copy model correctly
 *
 * @export
 * @param {string} currentUrl
 * @return {*}  {string}
 */
export function getTypeStorageUrlSuffixForEditScreen(currentUrl: string): string {
  return getRoutePath(currentUrl.replace('/add', '/edit'));
}

/**
 * Helper function for lodash merger to handle arrays properly.
 * Otherwise, empty arrays are not merged correctly.
 *
 * @param {*} objValue
 * @param {*} srcValue
 * @return {*} 
 */
function mergeCustomizer(objValue:any, srcValue:any) {
  if (_.isArray(objValue)) {
    return srcValue;
  }
  else if (_.isObject(objValue)) {
    _.merge(objValue, srcValue);
  }
  if (srcValue === undefined) {
    return srcValue;
  }
}

function initializeStorageAndGet(storageName:string, initialValue:any) {
  if (fastStorage[storageName] !== undefined && fastStorage[storageName] !== null) {
    return fastStorage[storageName];
  }

  fastStorage[storageName] =  initialValue !== null && initialValue !== undefined ? _.cloneDeep(initialValue) : initialValue;
  return fastStorage[storageName];
}

/**
 * useTypedFastStorage - typed wrapper around useFastStorage
 *
 * @export
 * @template T
 * @param {string} storageName - unique name of the storage object
 * @param {(T|null)} [defaultValue=null] - default value to return initially if storage for given key is empty
 * @return {*}  - Array of currentState, setter function, cleanup function and object reference
 */
export function useTypedFastStorage<T>(storageNameParam:string, defaultValue:T|null = null): [T|null, (x:Partial<T>) => void, () => void, React.MutableRefObject<T|null>, number] {
  //<save-sqlinfo: useFastStorage
  //const [storageObj, _updateStorage, deleteObjFromStorage, , tick] = useFastStorage(storageName, defaultValue);
  //const storageObjRef = useRef<T|null>(null);

  const history = useHistory();

  const url = `${history.location.pathname}${history.location.search}`;
  // const storageName = !url.includes('common/joblistview')
  //   ? `${storageNameParam}-${getRoutePath(history.location.pathname)}`
  //   : storageNameParam;
  const storageName = !url.includes('common/joblistview') ? `${storageNameParam}-${url}` : storageNameParam;


  const [storageObj, _updateStorage, deleteObjFromStorage, storageObjRef, tick] = useFastStorage(storageName, defaultValue);
  //save-sqlinfo>

  // Don't send param with url prefixed - useDirtyCheck handles that internally
  const {getCleanState} = useDirtyCheck(storageNameParam); //[save-sqlinfo]

  useEffect(() => {
    storageObjRef.current = storageObj;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageObj]);

  const updateStorage = (value:Partial<T>) => {
    _updateStorage(value,
      //[save-sqlinfo]
      getCleanState());
  };

  const typedStorageObj = storageObj as T|null;

  return [typedStorageObj, updateStorage, deleteObjFromStorage, storageObjRef, tick];
}

/**
 * useFastStorage - storage management hook which uses React lifecycle but does not pass storage
 * object through Redux store, making it faster to use.
 *
 * @export
 * @param {string} storageName - unique name of the storage object
 * @param {(any|null)} [defaultValue=null] - default value to return initially if storage for given key is empty
 * @return {*} - Array of currentState, setter function, cleanup function and object reference
 */

type StorageUpdateFunction = (
  value: any, 
  orgEntity?: any, 
  options?: { 
    forceClone?: boolean 
  }
) => void;

type DeleteFunction = () => void;

export default function useFastStorage(
  storageName: string,
  defaultValue: any | null = null
): [any, StorageUpdateFunction, DeleteFunction, React.MutableRefObject<any>, number] {
  const initialValue = initializeStorageAndGet(storageName, defaultValue);
  const objRef = useRef(initialValue);

  const [tick, _setTick] = useState(0);
  const tickRef = useRef(0);

  const doTick = () => {
    tickRef.current = tickRef.current + 1;
    _setTick(tickRef.current);
  };

  const isTrackableObject = (value: any): boolean =>
    value !== null && typeof value === 'object';

  const updateStorageValue = (
    value: any,
    options?: {
      forceClone?: boolean
    }
  ) => {
    if (value === null || value === undefined) return;

    const isListData = storageName.includes('ifms-list-data-');
    const forceClone = options?.forceClone || false;

    if (!fastStorage[storageName] || typeof value !== 'object') {
      fastStorage[storageName] = value;
      objRef.current = value;
    } else {
      fastStorage[storageName] = isListData || forceClone
        ? _.cloneDeep(value)
        : _.mergeWith(fastStorage[storageName], value, mergeCustomizer);

      handleUndefinedValues(fastStorage[storageName], value);
      objRef.current = fastStorage[storageName];
    }
  };

  const updateStorage: StorageUpdateFunction = (value, orgEntity, options) => {
    updateStorageValue(value, options);

    if (isTrackableObject(objRef.current) && objRef.current.trackable) {
      Object.keys(value).forEach(prop => {
        checkEntityPropChanges(prop, orgEntity, objRef.current);
      });
    }
    
    doTick();
  };

  const deleteObjFromStorage: DeleteFunction = () => {
    delete fastStorage[storageName];
    objRef.current = null;
    doTick();
  };

  return [
    fastStorage[storageName],
    updateStorage,
    deleteObjFromStorage,
    objRef,
    tick
  ];
}

/**
 * 
 * @param resultObj Lodash merge/mergeWith ignores undefined properties so those are applied manually here
 * @param value 
 */
function handleUndefinedValues(resultObj: any, value: any) {
  if (!resultObj || !value) {
    return;
  }

  if (!_.isObject(value) || _.isArray(value)) {
    return;
  }

  for(const key of _.keys(value)) {

    // If it is an object and it exists in resultObj, recursively check
    if (_.isObject((value as any)[key]) && !_.isArray((value as any)[key]) && _.isObject(resultObj[key]) && !_.isArray(resultObj[key])) {
      handleUndefinedValues(resultObj[key], (value as any)[key]);
    }

    if ((value as any)[key] === undefined) {
      resultObj[key] = undefined;

    }
  }
}
